import { create } from 'zustand';

/**
 * notification store — in-app inbox 의 frontend 측 SSOT (실시간 unread 카운터).
 *
 * 사용:
 *   const unreadCount = useNotificationStore((s) => s.unreadCount);
 *   useNotificationStore.getState().incrementUnread();
 *
 * 동작:
 *   - useSSE 의 onNotification callback 이 incrementUnread() 호출 → bell badge 즉각 갱신
 *   - 초기 mount 시 GET /api/v1/notifications/unread-count 로 setUnread(N) 동기화 (별도 cycle)
 *   - mark-read 호출 후 결과 카운트로 setUnread (또는 decrementUnread)
 *
 * dual-trail (DB inbox + 실시간 SSE) 의 frontend SSOT.
 * 근거 학습: docs/knowledge/pattern-cross-domain-notification-trigger-via-helper-export-with-self-notify-guard.md
 */

interface NotificationState {
  unreadCount: number;
  /**
   * SSE 도달 시 monotonically 증가하는 카운터. dropdown 등 watcher 가 deps 로 사용해
   * open 상태에서 새 알림 prepend (또는 refetch) 트리거.
   */
  lastNotificationBump: number;
  setUnread: (count: number) => void;
  /** SSE 도달 시 사용 — unreadCount +1 + lastNotificationBump +1 (dropdown 같은 watcher 깨움). */
  incrementUnread: () => void;
  decrementUnread: () => void;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  unreadCount: 0,
  lastNotificationBump: 0,
  setUnread: (count) => set({ unreadCount: Math.max(0, count) }),
  incrementUnread: () =>
    set((s) => ({
      unreadCount: s.unreadCount + 1,
      lastNotificationBump: s.lastNotificationBump + 1,
    })),
  decrementUnread: () => set((s) => ({ unreadCount: Math.max(0, s.unreadCount - 1) })),
  reset: () => set({ unreadCount: 0, lastNotificationBump: 0 }),
}));
